import { z } from 'zod';
import { ApiClient } from '../lib/api';

const THUMBNAIL_WIDTH = 468;
const THUMBNAIL_HEIGHT = 351;

// Schema definitions
export const UploadedFileSchema = z.object({
  fileName: z.string(),
  key: z.string(),
  size: z.number(),
  contentType: z.string(),
  isImage: z.boolean().optional(),
  isThumbnail: z.boolean().optional()
});

export const UploadResponseSchema = z.object({
  message: z.string(),
  files: UploadedFileSchema.array()
});

// Add presigned URL schema
export const PresignedUrlSchema = z.object({
  key: z.string(),
  getURL: z.string(),
  putURL: z.string()
});

export const GetPresignedUrlResponseSchema = z.object({
  presignedURLs: PresignedUrlSchema.array()
});

export const PostPresignedUrlResponseSchema = z.object({
  presignedUrls: PresignedUrlSchema.array()
});

export const DeleteResponseSchema = z.object({
  message: z.string()
});

export type UploadedFile = z.infer<typeof UploadedFileSchema>;
export type UploadResponse = z.infer<typeof UploadResponseSchema>;
export type PresignedUrl = z.infer<typeof PresignedUrlSchema>;
export type GetPresignedUrlResponse = z.infer<typeof GetPresignedUrlResponseSchema>;
export type PostPresignedUrlResponse = z.infer<typeof PostPresignedUrlResponseSchema>;
export type DeleteResponse = z.infer<typeof DeleteResponseSchema>;

export class S3Service {
  private readonly api: ApiClient;

  constructor(baseURL: string, stage: string) {
    this.api = new ApiClient(`${baseURL}/${stage}/s3-service-${stage}`, stage);
  }

  async uploadFiles(
    prefix: string,
    files: { file: File, key: string, isImage?: boolean, isThumbnail?: boolean }[],
    onProgress?: (progress: number) => void
  ): Promise<UploadResponse> {

    const thumbs: { file: File, key: string, isImage?: boolean, isThumbnail?: boolean }[] = []

    // Process files and create thumbnails when needed
    await Promise.all(files.map(async (file) => {
      const randomNum = Math.floor(111111 + Math.random() * 999999);

      file.key = `${randomNum}_${file.key}`;

      if (file.isImage && file.file.type.startsWith('image/')) {
        const thumbnailKey = `TH_${file.key}`;
        
        // Create a thumbnail by resizing the image using canvas
        const createThumbnail = async (imageFile: File): Promise<Blob> => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
              reject(new Error('Could not get canvas context'));
              return;
            }
            
            img.onload = () => {
              // Calculate dimensions while maintaining aspect ratio
              let width = img.width;
              let height = img.height;
              
              // Determine which dimension to constrain
              if (width > height) {
                // Landscape orientation
                if (width > THUMBNAIL_WIDTH) {
                  height = Math.round(height * (THUMBNAIL_WIDTH / width));
                  width = THUMBNAIL_WIDTH;
                }
              } else {
                // Portrait or square orientation
                if (height > THUMBNAIL_HEIGHT) {
                  width = Math.round(width * (THUMBNAIL_HEIGHT / height));
                  height = THUMBNAIL_HEIGHT;
                }
              }
              
              // Set canvas dimensions to the calculated size
              canvas.width = width;
              canvas.height = height;
              
              // Draw the image on the canvas with the calculated dimensions
              ctx.drawImage(img, 0, 0, width, height);
              
              // Convert to JPG format
              canvas.toBlob(
                (blob) => {
                  if (blob) {
                    resolve(blob);
                  } else {
                    reject(new Error('Could not create thumbnail blob'));
                  }
                },
                'image/jpeg',
                0.85 // Quality parameter (0.85 = 85% quality)
              );
            };
            
            img.onerror = () => reject(new Error('Error loading image'));
            
            // Load the image from the file
            const url = URL.createObjectURL(imageFile);
            img.src = url;
          });
        };
        
        // Create the thumbnail
        const thumbnailBlob = await createThumbnail(file.file);
        const thumbnailFile = new File([thumbnailBlob], thumbnailKey, { type: 'image/jpeg' });
        thumbs.push({ file: thumbnailFile, key: thumbnailKey, isImage: file.isImage, isThumbnail: true });
      }
    }));

    files = [...files, ...thumbs];

    const keys = files.map(file => ({
      key: `${prefix}/${file.key}`
    }));

    const preSignedUrls = await this.api.request(
      {
        method: 'POST',
        url: `/s3/keys`,
        data: JSON.stringify({
          keys
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      },
      PostPresignedUrlResponseSchema
    );

    // Calculate total size for progress tracking
    const totalSize = files.reduce((sum, file) => sum + file.file.size, 0);
    let totalUploaded = 0;
    // Track uploaded bytes per file
    const uploadedBytesByFile: Record<string, number> = {};

    await Promise.all(preSignedUrls.presignedUrls.map(async (presignedUrl) => {
      const file = files.find((file) => `${prefix}/${file.key}` === presignedUrl.key);
      if (!file) {
        throw new Error('File not found');
      }
      // Initialize the uploaded bytes for this file
      uploadedBytesByFile[file.key] = 0;

      // Use XMLHttpRequest for better control over progress and compatibility with S3 presigned URLs
      return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.open('PUT', presignedUrl.putURL);
        xhr.setRequestHeader('Content-Type', file.file.type);
        
        xhr.upload.onprogress = (event) => {
          if (!onProgress) return;
          
          // Calculate how many new bytes were uploaded in this progress event
          const previousUploaded = uploadedBytesByFile[file.key];
          const newlyUploaded = event.loaded - previousUploaded;
          
          // Update tracking variables
          uploadedBytesByFile[file.key] = event.loaded;
          totalUploaded += newlyUploaded;
          
          // Calculate overall percentage based on total size
          const percentCompleted = Math.round((totalUploaded * 100) / totalSize);
          onProgress(percentCompleted);
        };
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`HTTP error ${xhr.status}: ${xhr.statusText}`));
          }
        };
        
        xhr.onerror = () => {
          reject(new Error('Network error occurred during upload'));
        };
        
        xhr.send(file.file);
      });
    }));

    return {
      message: 'Files uploaded successfully',
      files: files.map((file) => ({
        fileName: file.file.name,
        key: file.key,
        size: file.file.size,
        contentType: file.file.type,
        isImage: file.isImage,
        isThumbnail: file.isThumbnail
      }))
    };
  }

  async getPresignedUrl(key: string): Promise<GetPresignedUrlResponse> {
    return this.api.request(
      {
        method: 'GET',
        url: `/s3/presigned-url?key=${encodeURIComponent(key)}`,
      },
      GetPresignedUrlResponseSchema
    );
  }

  async deleteObject(key: string): Promise<DeleteResponse> {
    return this.api.request(
      {
        method: 'DELETE',
        url: `/s3/object?key=${encodeURIComponent(key)}`,
      },
      DeleteResponseSchema
    );
  }
}
